import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, MessageSquareWarning, CheckCircle, Loader2, MonitorX, WifiOff } from 'lucide-react';
import { sendReport } from '../services/api';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import { getReportSubmissionError, type ReportSubmissionError } from '../utils/reportUiState';
import type { User } from 'firebase/auth';
import type { Message, ReportCategory, ReportPayload } from '../types';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  chatId: string | null;
  messages: Message[];
}

export const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, user, chatId, messages }) => {
  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [submitError, setSubmitError] = useState<ReportSubmissionError | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const reportIdRef = useRef('');

  // [FIX] RESETEAR EL ESTADO CADA VEZ QUE SE ABRE EL MODAL
  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setDescription('');
      setCategory(null);
      setCategoryError(false);
      setSubmitError(null);
      setIsSubmitting(false);
      reportIdRef.current = crypto.randomUUID();
    }
  }, [isOpen]);

  useModalAccessibility(dialogRef, { enabled: isOpen, onClose });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) {
      setCategoryError(true);
      return;
    }
    if (!description.trim() || !user) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const lastMessages = messages.slice(-6).map(m =>
        `[${m.sender.toUpperCase()}]: ${m.text || (m.imageUrl ? '[Imagen]' : '[Audio]')}`
      ).join('\n').slice(0, 20_000);

      const payload: ReportPayload = {
        reportId: reportIdRef.current || crypto.randomUUID(),
        chatId: chatId || "sin_chat_id",
        category,
        description,
        context: lastMessages,
        timestamp: new Date().toISOString()
      };

      await sendReport(payload);

      setSuccess(true);
      setIsSubmitting(false);

    } catch (error) {
      setSubmitError(getReportSubmissionError(error));
      setIsSubmitting(false);
      window.setTimeout(() => submitErrorRef.current?.focus(), 0);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
        tabIndex={-1}
        className="bg-panel rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-hidden transform transition-all flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >


        {/* Header */}
        <div className="bg-action p-4 flex justify-between items-center text-white">
          <h2 id="report-modal-title" className="flex items-center gap-2 font-bold text-lg">
            <AlertTriangle size={20} className="text-warning" />
            Reportar Incidencia
          </h2>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center hover:bg-action-hover rounded-full transition-colors" aria-label="Cerrar reporte">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center motion-fade-through">
              <div className="w-16 h-16 bg-success-soft text-success rounded-full flex items-center justify-center mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-ink">Reporte guardado</h3>
              <p className="text-muted mt-2 mb-6">Gracias por ayudarnos a mejorar Juris.</p>

              <button
                onClick={onClose}
                className="min-h-11 px-6 py-2 bg-action text-white rounded-xl font-bold hover:bg-action-hover transition-colors shadow-lg shadow-black"
              >
                Volver al Chat
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isSubmitting}>

              {submitError && (
                <div
                  ref={submitErrorRef}
                  role="alert"
                  tabIndex={-1}
                  className="rounded-xl border border-danger bg-danger-soft p-3 text-danger shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
                >
                  <div className="flex items-start gap-3">
                    <WifiOff size={20} className="mt-0.5 shrink-0 text-danger" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-bold">{submitError.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-danger">{submitError.message}</p>
                      {submitError.requestId && (
                        <p className="mt-2 text-xs text-danger">Referencia: {submitError.requestId}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Botones Selección */}
              <div>
                <div className={`grid grid-cols-3 gap-3 rounded-xl transition-all ${categoryError ? 'ring-1 ring-danger p-1' : ''}`}>
                  <button type="button" aria-pressed={category === 'content'} onClick={() => { setCategory('content'); setCategoryError(false); }} className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${category === 'content' ? 'border-juris bg-juris-soft text-ink' : categoryError ? 'border-danger hover:border-line text-muted' : 'border-line hover:border-line text-muted'}`}>
                    <MessageSquareWarning size={24} className="mb-2" />
                    <span className="text-xs font-semibold text-center leading-tight">Respuesta<br />Imprecisa</span>
                  </button>
                  <button type="button" aria-pressed={category === 'technical'} onClick={() => { setCategory('technical'); setCategoryError(false); }} className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${category === 'technical' ? 'border-juris bg-juris-soft text-ink' : categoryError ? 'border-danger hover:border-line text-muted' : 'border-line hover:border-line text-muted'}`}>
                    <MonitorX size={24} className="mb-2" />
                    <span className="text-xs font-semibold text-center leading-tight">Fallo<br />Técnico</span>
                  </button>
                  <button type="button" aria-pressed={category === 'suggestion'} onClick={() => { setCategory('suggestion'); setCategoryError(false); }} className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${category === 'suggestion' ? 'border-juris bg-juris-soft text-ink' : categoryError ? 'border-danger hover:border-line text-muted' : 'border-line hover:border-line text-muted'}`}>
                    <CheckCircle size={24} className="mb-2" />
                    <span className="text-xs font-semibold text-center leading-tight">Sugerencia<br />Mejora</span>
                  </button>
                </div>
                {categoryError && (
                  <p role="alert" className="text-danger text-xs mt-1.5 font-medium motion-fade-through-fast pl-0.5">Selecciona el tipo de incidencia</p>
                )}
              </div>

              {/* Text Area */}
              <div>
                <label htmlFor="report-description" className="block text-sm font-medium text-ink mb-1">Descripción</label>
                <textarea
                  id="report-description"
                  name="report-message"
                  className="w-full border border-line rounded-xl p-3 text-sm focus:ring-2 focus:ring-juris outline-none resize-none h-32"
                  placeholder="Describe el error o sugiere..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  maxLength={2000}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 min-h-11 px-4 py-2.5 bg-raised text-ink rounded-xl font-medium hover:bg-raised transition-colors">Cancelar</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 min-h-11 px-4 py-2.5 bg-action text-white rounded-xl font-medium hover:bg-action-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                      <span>Enviando…</span>
                    </>
                  ) : submitError ? 'Reintentar envío' : 'Enviar reporte'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
